import { ShaderBuilder } from "../shader_builder/shader_builder.js";
import { InstanceBatch } from "../core/batching/instance_batch.js";
import { InstanceController } from "../core/controllers/instance_controller.js";
import { BlendMode, AttributeSize } from "../core/types/webgl_types.js";
import { Vector2 } from "../../common/vector2.js";
import { WebGLUIRenderLayer } from "../core/runtime/webgl_ui_render_layer.js";
import { Vfx2DGlobalAtlas } from "../core/rendering/textures/vfx_2d_global_atlas.js";
import { WebGLStaticAtlasBuilder, IUVBounds } from "../core/rendering/textures/webgl_static_atlas_bulder.js";
import { ImgID } from "../../image_loader/img_id.js";

export class LockInstanceController extends InstanceController
{
  private batch_lock: WebGLUILock;

  constructor( batch: WebGLUILock, stable_id?: number | number[] )
  {
    super( batch, stable_id );
    this.batch_lock = batch;
  }

  public setTransformAndWobble( pos: Vector2, angle: number, size: Vector2, opacity: number ): void
  {
    const ids = Array.isArray( ( this as any ).stable_ids )
      ? ( this as any ).stable_ids
      : [ ( this as any ).stable_ids[0] ];

    for ( const id of ids )
      this.batch_lock.updateLockTransform( id, pos, angle, size, opacity );
  }
}

export interface IWebGLLockEntry
{
  position: Vector2;
  size_physical: Vector2;
  glint_angle_deg?: number;
  glint_width?: number;
  duration: number;
  intensity?: number;
  repeat_delay?: number;
  on_finish?: () => void;
}

export class WebGLUILock extends InstanceBatch
{
  private static readonly MAX_INSTANCES: number            = 5;
  private static readonly VERTICES_PER_INSTANCE: number    = 6;

  // Тюнінгові параметри відблиску
  private static readonly MASK_ALPHA_THRESHOLD: number     = 0.001;
  private static readonly MASK_SMOOTH_MIN: number          = 0.4;
  private static readonly MASK_SMOOTH_MAX: number          = 0.6;
  private static readonly SPECULAR_POWER: number           = 8.0;
  private static readonly SPECULAR_FACTOR: number          = 0.15;
  private static readonly DIRTY_RECT_EXPAND_FACTOR: number = 1.5;

  private static readonly DEFAULT_GLINT_ANGLE_DEG: number  = -45.0;
  private static readonly DEFAULT_GLINT_WIDTH: number      = 0.15;
  private static readonly DEFAULT_INTENSITY: number        = 1.0;
  private static readonly DEFAULT_REPEAT_DELAY: number     = 0;

  private callbacks: ( Function | null )[] = [];
  private glint_durations: number[]        = [];
  private glint_delays: number[]           = [];
  private lock_uv: IUVBounds | null        = null;
  private glint_uv: IUVBounds | null       = null;
  private ui_layer: WebGLUIRenderLayer;

  constructor( ui_layer: WebGLUIRenderLayer )
  {
    super( {
      max_instances: WebGLUILock.MAX_INSTANCES,
      primitive_type: WebGL2RenderingContext.TRIANGLES,
      vertices_per_instance: WebGLUILock.VERTICES_PER_INSTANCE,
      blend_mode: BlendMode.Normal,
      attribute_layout: [
        { location: 0, size: AttributeSize.Vec2,  name: "a_position" },
        { location: 1, size: AttributeSize.Vec2,  name: "a_size" },
        { location: 2, size: AttributeSize.Float, name: "a_time" },
        { location: 3, size: AttributeSize.Float, name: "a_model_rotation" },
        { location: 4, size: AttributeSize.Float, name: "a_width" },
        { location: 5, size: AttributeSize.Float, name: "a_intensity" },
        { location: 6, size: AttributeSize.Float, name: "a_opacity" },
        { location: 7, size: AttributeSize.Vec2,  name: "a_glint_rot" },
      ]
    } );

    this.ui_layer = ui_layer;

    this.buildShader( new ShaderBuilder()
      .withUniform( "sampler2D", "u_atlas" )
      .withUniform( "vec4", "u_lock_uv" )
      .withUniform( "vec4", "u_glint_uv" )
      .withVarying( "vec2", "v_local01" )
      .withVarying( "vec2", "v_glint_rot", "flat" )
      .withVarying( "float", "v_time", "flat" )
      .withVarying( "float", "v_px2uv_y", "flat" )
      .withVarying( "float", "v_width", "flat" )
      .withVarying( "float", "v_intensity", "flat" )
      .withVarying( "float", "v_opacity", "flat" )
      .withVertexLogic( `
        float t = clamp( a_time, 0.0, 1.0 );
        
        vec2 pivot  = a_position + vec2( 0.0, -a_size.y * 0.5 );
        vec2 offset = a_position - pivot;
        
        float c = cos( a_model_rotation );
        float s = sin( a_model_rotation );
        vec2 rotated_offset = vec2(
          offset.x * c - offset.y * s,
          offset.x * s + offset.y * c
        );
        
        position = pivot + rotated_offset;
        size     = a_size;
        angle    = a_model_rotation;

        v_local01    = uv;
        v_time       = t;
        v_px2uv_y    = a_size.y > 0.0 ? 1.0 / a_size.y : 0.0;
        v_width      = a_width;
        v_intensity  = a_intensity;
        v_opacity    = a_opacity;
        v_glint_rot  = a_glint_rot;
      ` )
      .withFragmentLogic( `
        vec2 lock_uv = vec2(
          mix( u_lock_uv.x, u_lock_uv.z, v_local01.x ),
          mix( u_lock_uv.y, u_lock_uv.w, v_local01.y )
        );
        vec4 crown_mask = texture( u_atlas, lock_uv );
        if ( crown_mask.a <= ${WebGLUILock.MASK_ALPHA_THRESHOLD.toFixed( 4 )} )
          discard;

        vec2 uv_local = v_local01 - vec2( 0.5 );

        // Поворот локальних UV координат на кут лінії відблиску
        vec2 rot_uv = vec2(
          uv_local.x * v_glint_rot.x - uv_local.y * v_glint_rot.y,
          uv_local.x * v_glint_rot.y + uv_local.y * v_glint_rot.x
        );

        float bound_pad = 0.75 * ( abs( v_glint_rot.x ) + abs( v_glint_rot.y ) );
        float soft      = max( 1e-5, v_width );
        float bound     = bound_pad + soft;
        float shine_pos = mix( -bound, bound, v_time );
        
        float d         = abs( rot_uv.y - shine_pos );
        float local_pos = rot_uv.x + 0.5;

        vec2 glint_uv = vec2(
          mix( u_glint_uv.x, u_glint_uv.z, 0.5 ),
          mix( u_glint_uv.y, u_glint_uv.w, clamp( local_pos, 0.0, 1.0 ) )
        );
        
        float in_glint      = step( 0.0, local_pos ) * step( local_pos, 1.0 );
        float glint_profile = texture( u_atlas, glint_uv ).a * in_glint;
        float band          = 1.0 - smoothstep( 0.0, soft, d );

        float shine       = glint_profile * band;
        float mask        = smoothstep( ${WebGLUILock.MASK_SMOOTH_MIN.toFixed( 2 )}, ${WebGLUILock.MASK_SMOOTH_MAX.toFixed( 2 )}, crown_mask.a );
        float alpha_glint = shine * mask;

        vec3 raw_rgb = crown_mask.rgb / ( crown_mask.a + 1e-6 );
        float spec   = pow( shine, ${WebGLUILock.SPECULAR_POWER.toFixed( 1 )} ) * ${WebGLUILock.SPECULAR_FACTOR.toFixed( 2 )} * v_intensity;
        
        vec3 final_rgb    = raw_rgb + ( raw_rgb * v_intensity + vec3( spec ) ) * alpha_glint;
        float final_alpha = crown_mask.a * v_opacity;

        fragColor = vec4( final_rgb * final_alpha, final_alpha );
      ` )
    );
  }

  protected override onInit( gl: WebGL2RenderingContext ): void
  {
    const atlas_data = Vfx2DGlobalAtlas.getAtlasData();
    const atlas      = WebGLStaticAtlasBuilder.createAtlasFromData( gl, atlas_data );

    this.textures.push( { location: "u_atlas", texture: atlas.texture, unit: 0 } );
    this.lock_uv  = atlas_data.getUVRect( ImgID.UI_ChainOfferLockIcon );
    this.glint_uv = atlas_data.getUVRect( ImgID.LineGlow1 );

    this.callbacks       = new Array( this.max_instances ).fill( null );
    this.glint_durations = new Array( this.max_instances ).fill( 0 );
    this.glint_delays    = new Array( this.max_instances ).fill( 0 );
  }

  protected override onBeforeRender( gl: WebGL2RenderingContext ): void
  {
    if ( this.lock_uv )
    {
      gl.uniform4f(
        this.shader.getLocation( gl, "u_lock_uv" ),
        this.lock_uv.min_x,
        this.lock_uv.min_y,
        this.lock_uv.max_x,
        this.lock_uv.max_y
      );
    }

    if ( this.glint_uv )
    {
      gl.uniform4f(
        this.shader.getLocation( gl, "u_glint_uv" ),
        this.glint_uv.min_x,
        this.glint_uv.min_y,
        this.glint_uv.max_x,
        this.glint_uv.max_y
      );
    }
  }

  public spawn( entry: IWebGLLockEntry, start_time: number ): LockInstanceController
  {
    const repeat_delay = entry.repeat_delay ?? WebGLUILock.DEFAULT_REPEAT_DELAY;
    const is_repeating = repeat_delay > 0;
    const duration     = is_repeating ? 0 : entry.duration;

    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 )
      return new LockInstanceController( this, -1 );

    this.callbacks[idx] = entry.on_finish ?? null;

    const fpi      = this.buffer.floats_per_instance;
    const offset   = idx * fpi;
    const gpu_data = this.buffer.getDataArray();

    const glint_angle_deg = entry.glint_angle_deg ?? WebGLUILock.DEFAULT_GLINT_ANGLE_DEG;
    const glint_rad       = ( glint_angle_deg * Math.PI ) / 180.0;
    const glint_width     = entry.glint_width ?? WebGLUILock.DEFAULT_GLINT_WIDTH;
    const intensity       = entry.intensity ?? WebGLUILock.DEFAULT_INTENSITY;

    gpu_data[offset + 0]  = entry.position.x;
    gpu_data[offset + 1]  = entry.position.y;
    gpu_data[offset + 2]  = entry.size_physical.x;
    gpu_data[offset + 3]  = entry.size_physical.y;
    gpu_data[offset + 4]  = 0.0;
    gpu_data[offset + 5]  = 0.0;
    gpu_data[offset + 6]  = glint_width;
    gpu_data[offset + 7]  = intensity;
    gpu_data[offset + 8]  = 1.0;
    gpu_data[offset + 9]  = Math.cos( glint_rad );
    gpu_data[offset + 10] = Math.sin( glint_rad );

    this.glint_durations[idx] = entry.duration;
    this.glint_delays[idx]    = repeat_delay;
    this.buffer_dirty         = true;

    return new LockInstanceController( this, this.getStableId( idx ) );
  }

  public updateLockTransform( stable_id: number, pos: Vector2, angle: number, size: Vector2, opacity: number ): void
  {
    const idx = this.stable_to_idx.get( stable_id );
    if ( idx === undefined || idx === -1 )
      return;

    const fpi      = this.buffer.floats_per_instance;
    const offset   = idx * fpi;
    const gpu_data = this.buffer.getDataArray();

    gpu_data[offset + 0] = pos.x;
    gpu_data[offset + 1] = pos.y;
    gpu_data[offset + 2] = size.x;
    gpu_data[offset + 3] = size.y;
    gpu_data[offset + 5] = angle;
    gpu_data[offset + 8] = opacity;
    this.buffer_dirty    = true;
  }

  protected override updateInstance( gpu_data: Float32Array, offset: number, _progress: number, now: number, idx: number ): boolean
  {
    const duration = this.glint_durations[idx];
    const delay    = this.glint_delays[idx];

    // Якщо це одноразова анімація і час вийшов — знищуємо інстанс
    if ( delay <= 0 )
    {
      const elapsed = now - this.start_times[idx];
      if ( elapsed >= duration )
        return false;

      const t              = Math.min( 1.0, elapsed / duration );
      gpu_data[offset + 4] = t;
    }
    else
    {
      const total_cycle = duration + delay;
      if ( total_cycle > 0 && now > this.start_times[idx] + total_cycle )
      {
        const elapsed_since_start = now - this.start_times[idx];
        const cycles_passed       = Math.floor( elapsed_since_start / total_cycle );
        this.start_times[idx]    += cycles_passed * total_cycle;
      }

      const elapsed        = now - this.start_times[idx];
      const t              = Math.min( 1.0, elapsed / duration );
      gpu_data[offset + 4] = t;
    }

    const px = gpu_data[offset + 0];
    const py = gpu_data[offset + 1];
    const sx = gpu_data[offset + 2];
    const sy = gpu_data[offset + 3];
    
    const radius = Math.max( sx, sy ) * WebGLUILock.DIRTY_RECT_EXPAND_FACTOR;
    this.ui_layer.addDirtyRect( px - radius, py - radius, radius * 2, radius * 2 );

    return true;
  }

  protected onSwapInstance( idx_a: number, idx_b: number ): void
  {
    const temp_cb = this.callbacks[idx_a];
    this.callbacks[idx_a] = this.callbacks[idx_b];
    this.callbacks[idx_b] = temp_cb;

    const temp_dur = this.glint_durations[idx_a];
    this.glint_durations[idx_a] = this.glint_durations[idx_b];
    this.glint_durations[idx_b] = temp_dur;

    const temp_delay = this.glint_delays[idx_a];
    this.glint_delays[idx_a] = this.glint_delays[idx_b];
    this.glint_delays[idx_b] = temp_delay;
  }

  protected onInstanceDeath( idx: number ): void
  {
    const cb = this.callbacks[idx];
    if ( cb )
    {
      cb();
      this.callbacks[idx] = null;
    }
    
    const fpi      = this.buffer.floats_per_instance;
    const offset   = idx * fpi;
    const gpu_data = this.buffer.getDataArray();
    const px       = gpu_data[offset + 0];
    const py       = gpu_data[offset + 1];
    const sx       = gpu_data[offset + 2];
    const sy       = gpu_data[offset + 3];
    const radius   = Math.max( sx, sy ) * WebGLUILock.DIRTY_RECT_EXPAND_FACTOR;
    this.ui_layer.addDirtyRect( px - radius, py - radius, radius * 2, radius * 2 );
  }

  public override dispose( gl: WebGL2RenderingContext ): void
  {
    super.dispose( gl );
  }
}
